import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, CreditCard, ExternalLink, MapPin, Package, Phone, RefreshCw, ShoppingBag, Truck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { getProductImage } from '../utils/productImages';
import { getErrorMessage, withTimeout } from '../utils/safeAsync';
import { ShipmentService } from '../services/shipmentService';

const getOrderAddress = (order: any) => {
  if (order.shipping_address) return order.shipping_address;
  if (!order.address) return {};
  if (typeof order.address !== 'string') return order.address;

  try {
    return JSON.parse(order.address);
  } catch {
    return { address_line_1: order.address };
  }
};

const Orders: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('success') === '1') {
      showToast('Order successful');
    }
  }, [searchParams, showToast]);

  const fetchOrders = async () => {
    if (!user) {
      setOrders([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await withTimeout(supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_id,
            product_name,
            product_image,
            product_price,
            quantity,
            price,
            products (
              id,
              name,
              image
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
        10000,
        'Orders load timed out'
      );

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', getErrorMessage(error));
      setOrders([]);
      showToast('Could not load orders. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user, showToast]);

  const handleRefreshTracking = async (orderId: string) => {
    setTrackingOrderId(orderId);
    try {
      const result = await ShipmentService.refreshTracking(orderId);
      if (!result.success) {
        showToast(result.error || 'Tracking is not available yet.', 'info');
      } else {
        showToast('Tracking updated');
      }
      await fetchOrders();
    } catch (error) {
      console.error('Error refreshing tracking:', getErrorMessage(error));
      showToast('Could not refresh tracking right now.', 'error');
    } finally {
      setTrackingOrderId(null);
    }
  };

  const getOrderStatus = (order: any) => order.order_status || order.status || 'processing';
  const getPaymentStatus = (order: any) => order.payment_status || (order.payment_id ? 'Paid' : 'Pending');
  const getPaymentMethod = (order: any) => order.payment_method || (order.payment_id ? 'Razorpay' : 'COD');
  const isPaidStatus = (status: string) => ['paid', 'completed', 'success', 'successful'].includes(status.toLowerCase());

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <ShoppingBag className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-gray-900 hover:text-black mb-6">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
        </div>

        {orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow overflow-hidden p-8 text-center"
          >
            <Package className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-medium text-gray-900 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-6">Your order history will appear here after payment.</p>
            <Link
              to="/shop"
              className="inline-flex items-center px-6 py-3 rounded-md shadow-sm text-white bg-gray-900 hover:bg-black font-medium"
            >
              Continue Shopping
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {orders.map((order: any) => {
              const orderStatus = getOrderStatus(order);
              const paymentStatus = getPaymentStatus(order);
              const paymentMethod = getPaymentMethod(order);
              const paymentPaid = isPaidStatus(paymentStatus);
              const address = getOrderAddress(order);

              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
                >
                  <div className="bg-gray-900 px-6 py-4 text-white">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Package className="w-5 h-5" />
                          <h3 className="text-lg font-semibold">
                            Order #{order.order_number || order.id?.substring(0, 8).toUpperCase()}
                          </h3>
                        </div>
                        <p className="mt-1 text-xs text-gray-300">Order ID: {order.id}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          {orderStatus}
                        </span>
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                          paymentPaid ? 'bg-green-500/20 text-green-100' : 'bg-yellow-500/20 text-yellow-100'
                        }`}>
                          {paymentPaid ? 'Payment Successful' : 'Payment Pending'}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-gray-300">
                      {new Date(order.created_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>

                  <div className="p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <ShoppingBag className="w-5 h-5 mr-2 text-gray-900" />
                      Order Items
                    </h4>
                    <div className="space-y-4">
                      {order.order_items?.map((item: any, index: number) => {
                        const name = item.product_name || item.products?.name || 'Product';
                        const image = item.product_image || item.products?.image || '';
                        const price = Number(item.product_price || item.price || 0);

                        return (
                          <div
                            key={`order-${order.id}-item-${item.id}-${index}`}
                            className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                          >
                            <img
                              src={getProductImage(image)}
                              alt={name}
                              className="w-16 h-16 object-contain rounded-lg bg-white shadow-sm"
                              loading="lazy"
                            />
                            <div className="flex-1 min-w-0">
                              <h5 className="text-base font-medium text-gray-800 mb-1">{name}</h5>
                              <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                                <span>Qty: {item.quantity}</span>
                                <span>Price: ₹{price.toLocaleString('en-IN')}</span>
                                <span className="font-semibold text-gray-900">
                                  Total: ₹{(price * item.quantity).toLocaleString('en-IN')}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-gray-50 border-t border-gray-200">
                    <div className="md:col-span-2">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                        <Truck className="w-4 h-4 mr-2 text-gray-900" />
                        Delivery Address
                      </h4>
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 text-sm text-gray-600">
                        <div className="flex items-center text-gray-900 font-medium">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                          {address.fullName || address.full_name || 'N/A'}
                        </div>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>{address.houseNo || address.address_line_1}</p>
                          <p>{address.apartment || address.address_line_2}</p>
                          <p>{address.area || address.landmark}</p>
                          <p>{address.city}, {address.state} - {address.pincode}</p>
                          <div className="flex items-center pt-1">
                            <Phone className="w-4 h-4 text-gray-400 mr-2" />
                            {address.mobileNumber || address.phone}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                        <CreditCard className="w-4 h-4 mr-2 text-gray-900" />
                        Payment & Total
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                          <p className="text-sm text-gray-600 mb-1">Payment Method</p>
                          <p className="text-base font-semibold text-gray-900">
                            {paymentMethod}
                          </p>
                          <p className="mt-1 text-xs font-medium text-gray-600">
                            Status: {paymentStatus}
                          </p>
                          {order.payment_id && (
                            <p className="mt-2 text-xs font-medium text-gray-800 break-all">
                              Payment ID: {order.payment_id}
                            </p>
                          )}
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                          <p className="text-sm text-gray-600 mb-1">Order Total</p>
                          <p className="text-xl font-bold text-gray-900">
                            ₹{Number(order.total_amount || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 bg-white p-6">
                    <h4 className="mb-4 flex items-center text-sm font-semibold text-gray-800">
                      <Truck className="mr-2 h-4 w-4 text-gray-900" />
                      Shipment Tracking
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                        <p className="text-xs font-medium uppercase text-gray-500">Courier</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">
                          {order.courier_name || 'Not assigned yet'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                        <p className="text-xs font-medium uppercase text-gray-500">AWB</p>
                        <p className="mt-1 break-all text-sm font-semibold text-gray-900">
                          {order.awb_number || '-'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                        <p className="text-xs font-medium uppercase text-gray-500">Shipment Status</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">
                          {order.shipment_status || 'Pending'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                        <p className="text-xs font-medium uppercase text-gray-500">Tracking</p>
                        <div className="mt-2 flex flex-wrap gap-3">
                          {order.tracking_url ? (
                            <a
                              href={order.tracking_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-sm font-semibold text-blue-700 hover:text-blue-900"
                            >
                              Track Order
                              <ExternalLink className="ml-1 h-4 w-4" />
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500">Unavailable</span>
                          )}
                          {order.awb_number && (
                            <button
                              type="button"
                              onClick={() => handleRefreshTracking(order.id)}
                              disabled={trackingOrderId === order.id}
                              className="inline-flex items-center text-sm font-semibold text-gray-800 hover:text-black disabled:opacity-50"
                            >
                              <RefreshCw className={`mr-1 h-4 w-4 ${trackingOrderId === order.id ? 'animate-spin' : ''}`} />
                              Refresh
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
